import { useTranslation } from "react-i18next";
import { AppLink } from "../lib/route-primitives";
import { Clapperboard, Eye, Pause, Play } from "lucide-react";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";
import { useWedding } from "../contexts/wedding-context";
import { useRejiWs } from "../hooks/use-reji-ws";

const card = "bg-card dark:bg-[rgba(27,24,23,0.75)] dark:backdrop-blur-xl border border-border dark:border-primary/20 shadow-sm rounded-2xl overflow-hidden";

/**
 * Dashboard glance at the Sahne — live over a silent observer WebSocket
 * (mode=observe: no polling, doesn't count as a viewer, doesn't raise the
 * Reji badge, never wakes a parked room). One CTA into the Reji console.
 */
export function RejiCard() {
  const { t } = useTranslation();
  const { slug } = useWedding();
  const { slideState, presence, viewerCount } = useRejiWs(slug, { observe: true });

  const total = slideState?.total ?? 0;
  const offline = !slideState || total === 0;
  const paused = slideState?.paused ?? false;
  // Unified person-count: distinct guests, presence-first (viewer_count is
  // the same number from the same source — kept as a fallback).
  const guests = presence?.total ?? viewerCount;

  return (
    <div className={card}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Clapperboard className="w-4 h-4 text-primary" />
          {t("reji.dashboardStatus")}
        </h2>
        <div className="flex items-center gap-1.5">
          {presence?.rejiOnline && (
            <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
              <Clapperboard className="w-3 h-3" />
              {t("slideshow.rejiLive")}
            </span>
          )}
          {!offline && (
            <span
              className={cn(
                "flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0",
                paused
                  ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                  : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
              )}
            >
              {paused ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              {paused ? t("slideshowRemote.paused") : t("slideshowRemote.playing")}
            </span>
          )}
        </div>
      </div>

      <div className="px-5 py-4 flex items-center justify-between gap-3">
        {offline ? (
          <p className="text-xs text-muted-foreground">{t("slideshowRemote.offline")}</p>
        ) : (
          <div className="min-w-0">
            <p className="font-mono text-sm tabular-nums text-foreground">
              {t("slideshowRemote.slideOf", { current: (slideState?.index ?? 0) + 1, total })}
            </p>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
              <Eye className="w-3 h-3" />
              {t("slideshowRemote.viewers", { count: guests })}
            </p>
          </div>
        )}
        <AppLink
          to={paths.wedding.reji(slug)}
          className="shrink-0 flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold px-4 py-2.5 hover:opacity-90 transition-opacity cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          <Clapperboard className="w-4 h-4" />
          {t("reji.openReji")}
        </AppLink>
      </div>
    </div>
  );
}
